/**
 * The next-move editor (#114): what happens next, who owns it (a person, the customer or a contractor firm) and when
 * it is due (Today 5 pm, Next business day 9 am, +2 business days, A week, or a picked date-time). `NextMoveFields`
 * is the controlled form, reused inline by the Update panel; `NextMoveDialog` is the header's editor, which PUTs
 * /cases/{id}/next-move and refreshes the case header and the queue lanes.
 */
import { Button, Dialog, Input, SearchableSelect, type SearchableSelectOption } from '@patchbay/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import * as React from 'react'
import type { NextMoveIn, OwnerPerson, UserRef } from '../../lib/api/generated'
import { formatWhen } from '../../lib/display-time'
import { ApiError, caseApi, caseKeys, meDeskQuery } from '../case-view/api'
import { queueApi, queueKeys } from '../queue/queries'
import { dueForNextMovePreset, fromLocalInput, type NextMovePreset, PRESET_LABEL, toLocalInput } from './due'

export interface NextMoveValue {
  text: string
  /** `user:<id>` | `agent:<deskAgentId>` (a Desk-only tech, #226) | `customer` | `vendor` (#613) | `contractor:<id>` */
  owner: string
  /** datetime-local value */
  due: string
}

export interface NextMoveOwnerRef {
  owner_kind?: string | null
  owner_user_id?: string | null
  owner_desk_agent_id?: string | null
  owner_contractor_id?: string | null
  owner?: { id: string } | null
}

export const emptyNextMove = (meId?: string): NextMoveValue => ({
  text: '',
  owner: meId ? `user:${meId}` : '',
  due: toLocalInput(dueForNextMovePreset('next-business-day')),
})

export function ownerKindOf(owner: string): 'us' | 'customer' | 'contractor' | 'vendor' | null {
  if (owner.startsWith('user:') || owner.startsWith('agent:')) return 'us'
  if (owner === 'customer') return 'customer'
  if (owner === 'vendor') return 'vendor'
  if (owner.startsWith('contractor:')) return 'contractor'
  return null
}

/** The picker value for an API next-move owner. Empty means the API did not identify a concrete owner. */
export function ownerValueOf(owner: NextMoveOwnerRef | null | undefined): string {
  if (!owner) return ''
  if (owner.owner_kind === 'customer') return 'customer'
  // #613: an external hand-off stays vendor. Falling through to a user id would assign it to whoever accepts.
  if (owner.owner_kind === 'vendor') return 'vendor'
  if (owner.owner_kind === 'contractor' && owner.owner_contractor_id) return `contractor:${owner.owner_contractor_id}`
  if (owner.owner_user_id) return `user:${owner.owner_user_id}`
  if (owner.owner?.id) return `user:${owner.owner.id}`
  if (owner.owner_desk_agent_id) return `agent:${owner.owner_desk_agent_id}`
  return ''
}

/** The exact owner fields sent to the API; identifiers from another owner kind can never leak through. */
export function nextMoveOwnerIn(
  owner: string,
): Pick<NextMoveIn, 'owner_kind' | 'owner_user_id' | 'owner_desk_agent_id' | 'owner_contractor_id'> | null {
  if (owner.startsWith('user:')) {
    const id = owner.slice(5)
    return id ? { owner_kind: 'us', owner_user_id: id } : null
  }
  if (owner.startsWith('agent:')) {
    const id = owner.slice(6)
    return id ? { owner_kind: 'us', owner_desk_agent_id: id } : null
  }
  if (owner === 'customer') return { owner_kind: 'customer' }
  if (owner === 'vendor') return { owner_kind: 'vendor' }
  if (owner.startsWith('contractor:')) {
    const id = owner.slice(11)
    return id ? { owner_kind: 'contractor', owner_contractor_id: id } : null
  }
  return null
}

/** The API body, or null while a required field is missing (waiting needs text, owner and due: no half-moves). */
export function toNextMoveIn(v: NextMoveValue): NextMoveIn | null {
  const text = v.text.trim()
  const due = fromLocalInput(v.due)
  const owner = nextMoveOwnerIn(v.owner)
  return text && due && owner ? { text, due_at: due, ...owner } : null
}

export function humanMessage(e: unknown): string {
  if (e instanceof ApiError) return e.message || e.code
  return e instanceof Error ? e.message : 'Something went wrong'
}

export function useContractorFirms(caseUuid: string, enabled: boolean) {
  return useQuery({
    queryKey: ['queue', 'case', caseUuid, 'contractors'],
    queryFn: () => queueApi().contractors(caseUuid),
    enabled: enabled && Boolean(caseUuid),
    staleTime: 300_000,
    retry: false,
  })
}

/**
 * The one next-move owner picker used by the header, Update panel, after-send suggestions and proposal cards.
 * The contractor list is the case-ranked full directory: engaged firms lead, while search reaches every other firm.
 */
export function NextMoveOwnerPicker({
  value,
  onChange,
  people,
  staff,
  contractors,
  disabled,
  className,
  scope = 'owner',
  ariaLabel,
}: {
  value: string
  onChange: (owner: string) => void
  people: UserRef[]
  /** #226: Patchbay users plus Desk-only agents from the @mention directory. */
  staff?: OwnerPerson[]
  /** Case-ranked full contractor directory; undefined while loading and null when unreachable. */
  contractors?: { id: string; name: string }[] | null
  disabled?: boolean
  className?: string
  /** `people` is the eligible wrap-up directory: staff (including Desk-only) plus contractor firms. */
  scope?: 'owner' | 'people'
  ariaLabel?: string
}) {
  const peopleOnly = scope === 'people'
  const userIds = new Set(people.map((person) => person.id))
  const staffUsers = (staff ?? []).filter((person) => person.user_id && (peopleOnly || !userIds.has(person.user_id)))
  const deskOnly = (staff ?? []).filter((person) => !person.user_id)
  const ownerOptions: SearchableSelectOption[] = [
    ...(peopleOnly ? [] : people).map((person) => ({
      value: `user:${person.id}`,
      label: person.name,
      detail: person.email ?? undefined,
      group: 'Our team',
    })),
    ...staffUsers.map((person) => ({
      value: `user:${person.user_id}`,
      label: person.name,
      detail: person.email ?? undefined,
      group: 'Our team',
    })),
    ...deskOnly.map((person) => ({
      value: `agent:${person.desk_agent_id}`,
      label: person.name,
      detail: 'Desk only · @mentioned in Desk',
      group: 'Our team',
    })),
    ...(peopleOnly
      ? []
      : [
          { value: 'customer', label: 'The customer', group: 'Waiting on' },
          {
            value: 'vendor',
            label: 'External party',
            detail: 'Someone on the email who is not the customer or a contractor firm',
            group: 'Waiting on',
          },
        ]),
    ...(contractors ?? []).map((firm) => ({
      value: `contractor:${firm.id}`,
      label: firm.name,
      detail: 'Contractor firm',
      group: 'Waiting on',
    })),
  ]
  // Keep the current/likely pick first, then preserve the server's case-ranked ordering (#178, #259).
  ownerOptions.sort((left, right) => (left.value === value ? -1 : right.value === value ? 1 : 0))
  return (
    <SearchableSelect
      ariaLabel={ariaLabel ?? 'Next move owner'}
      value={value}
      onChange={onChange}
      options={ownerOptions}
      placeholder={peopleOnly ? 'Search staff or contractors' : 'Search people, customer or firms'}
      disabled={disabled}
      className={className}
    />
  )
}

type VisitChoice = 'move' | 'keep' | 'complete'

const VISIT_CHOICES: { id: VisitChoice; label: string; hint?: string }[] = [
  { id: 'move', label: 'Move the visit to this time' },
  { id: 'keep', label: 'Keep the visit, just change the next move', hint: 'The visit stays as it was.' },
  {
    id: 'complete',
    label: 'Visit is done: mark it complete and set this next move',
    hint: 'Records 0 minutes as unreported. No time is billed and no duplicate follow-up is created.',
  },
]

function sameInstant(left: string | null | undefined, right: string | null | undefined): boolean {
  if (!left || !right) return left === right
  return new Date(left).getTime() === new Date(right).getTime()
}

export function NextMoveFields({
  value,
  onChange,
  people,
  staff,
  contractors,
  autoFocus,
}: {
  value: NextMoveValue
  onChange: (v: NextMoveValue) => void
  people: UserRef[]
  /** #226: the @mention directory (Patchbay users + Desk agents); the Desk-only ones are listed after `people`. */
  staff?: OwnerPerson[]
  /** undefined while loading, null when unreachable */
  contractors?: { id: string; name: string }[] | null
  autoFocus?: boolean
}) {
  const set = (patch: Partial<NextMoveValue>) => onChange({ ...value, ...patch })
  return (
    <div className="space-y-3">
      <label className="block space-y-1 text-sm font-medium">
        What happens next
        <Input
          aria-label="Next move"
          autoFocus={autoFocus}
          maxLength={500}
          className="h-9"
          value={value.text}
          placeholder="e.g. Call the customer to confirm the phones ring"
          onChange={(e) => set({ text: e.currentTarget.value })}
        />
      </label>
      <div className="block space-y-1 text-sm font-medium">
        <span>Who owns it</span>
        <NextMoveOwnerPicker
          value={value.owner}
          onChange={(owner) => set({ owner })}
          people={people}
          staff={staff}
          contractors={contractors}
          className="h-9"
        />
        {contractors === null ? (
          <span className="block text-xs font-normal text-warning">Contractor list unreachable; a firm cannot be picked right now.</span>
        ) : null}
      </div>
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Due</legend>
        <div className="flex flex-wrap gap-1">
          {(Object.keys(PRESET_LABEL) as NextMovePreset[]).map((p) => (
            <Button key={p} type="button" size="sm" variant="outline" onClick={() => set({ due: toLocalInput(dueForNextMovePreset(p)) })}>
              {PRESET_LABEL[p]}
            </Button>
          ))}
        </div>
        <Input
          aria-label="Due date and time"
          type="datetime-local"
          className="h-9"
          value={value.due}
          onChange={(e) => set({ due: e.currentTarget.value })}
        />
      </fieldset>
    </div>
  )
}

export function NextMoveDialog({
  open,
  onClose,
  caseUuid,
  caseNumber,
  initial,
}: {
  open: boolean
  onClose: () => void
  caseUuid: string
  caseNumber: string
  initial?: { text?: string }
}) {
  const qc = useQueryClient()
  const form = useQuery({
    queryKey: ['case-update-form', caseUuid],
    queryFn: () => caseApi().updateForm(caseUuid),
    enabled: open && Boolean(caseUuid),
    retry: false,
  })
  const me = useQuery({ ...meDeskQuery(), enabled: open })
  const firms = useContractorFirms(caseUuid, open)
  const [value, setValue] = React.useState<NextMoveValue>(() => emptyNextMove())
  const [clearReason, setClearReason] = React.useState('')
  const [busy, setBusy] = React.useState<'save' | 'clear' | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [visitChoice, setVisitChoice] = React.useState<VisitChoice | null>(null)
  // A late update-form or "who am I" response must not wipe text the person has already typed.
  const edited = React.useRef(false)

  React.useEffect(() => {
    if (!open) {
      edited.current = false
      return
    }
    if (edited.current) return
    setError(null)
    setClearReason('')
    const nm = form.data?.next_move
    if (nm) {
      const owner = ownerValueOf(nm) || (me.data ? `user:${me.data.id}` : '')
      setValue({ text: nm.text, owner, due: nm.due_at ? toLocalInput(new Date(nm.due_at)) : emptyNextMove().due })
    } else {
      // #217: a new move is due when the case's SLA target falls due (response while unanswered, else resolution)
      const sla = form.data?.sla_due_at ? toLocalInput(new Date(form.data.sla_due_at)) : null
      setValue({ ...emptyNextMove(me.data?.id), text: initial?.text ?? '', ...(sla ? { due: sla } : {}) })
    }
  }, [open, form.data, me.data, initial?.text])

  const edit = (next: NextMoveValue) => {
    edited.current = true
    setValue(next)
  }

  const current = form.data?.next_move
  const visitId = current?.visit_id ?? null
  const requestedDue = value.due ? fromLocalInput(value.due) : null
  const dueChanged = Boolean(current && requestedDue && !sameInstant(current.due_at, requestedDue))
  // A different backing Visit starts a fresh explicit choice; reopening the dialog does too.
  // biome-ignore lint/correctness/useExhaustiveDependencies: visitId is the identity boundary for this choice
  React.useEffect(() => {
    if (open) setVisitChoice(null)
  }, [open, visitId])
  const needsVisitChoice = Boolean(visitId) && dueChanged
  const base = toNextMoveIn(value)
  const body: NextMoveIn | null =
    base && needsVisitChoice && visitChoice && visitId ? { ...base, linked_visit: { visit_id: visitId, action: visitChoice } } : base
  const save = async () => {
    if (!body || busy || (needsVisitChoice && !visitChoice)) return
    setBusy('save')
    setError(null)
    try {
      await caseApi().setNextMove(caseUuid, body)
      await Promise.all([
        qc.invalidateQueries({ queryKey: caseKeys.case(caseNumber) }),
        qc.invalidateQueries({ queryKey: queueKeys.all }),
        qc.invalidateQueries({ queryKey: ['case-update-form', caseUuid] }),
      ])
      onClose()
    } catch (e) {
      setError(humanMessage(e))
    } finally {
      setBusy(null)
    }
  }

  const clear = async () => {
    if (!form.data?.next_move || busy) return
    setBusy('clear')
    setError(null)
    try {
      await caseApi().clearNextMove(caseUuid, { reason: clearReason.trim() || null })
      await Promise.all([
        qc.invalidateQueries({ queryKey: caseKeys.case(caseNumber) }),
        qc.invalidateQueries({ queryKey: queueKeys.all }),
        qc.invalidateQueries({ queryKey: ['case-update-form', caseUuid] }),
      ])
      onClose()
    } catch (e) {
      setError(humanMessage(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()} title="Next move" description="What happens next, who owns it, and when.">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <NextMoveFields
          value={value}
          onChange={edit}
          people={form.data?.people ?? []}
          staff={form.data?.staff}
          contractors={firms.isError ? null : firms.data}
          autoFocus
        />
        {needsVisitChoice ? (
          <fieldset className="space-y-1.5 rounded-md border border-border p-3 text-sm">
            <legend className="px-1 text-xs font-medium text-muted-foreground">
              This next move is backed by a visit. What should happen to the visit?
            </legend>
            {VISIT_CHOICES.map((choice) => (
              <label key={choice.id} className="flex cursor-pointer items-start gap-2">
                <input
                  type="radio"
                  name="visit-choice"
                  className="mt-1"
                  checked={visitChoice === choice.id}
                  onChange={() => setVisitChoice(choice.id)}
                />
                <span>
                  {choice.label}
                  {choice.hint ? <span className="block text-xs text-muted-foreground">{choice.hint}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {form.data?.sla_due_at ? (
          <p className="text-xs text-muted-foreground">
            SLA target due {formatWhen(form.data.sla_due_at, { style: 'absolute' })}.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                edited.current = true
                setValue((v) => ({ ...v, due: toLocalInput(new Date(form.data?.sla_due_at ?? '')) }))
              }}
            >
              Use it
            </button>
          </p>
        ) : null}
        {ownerKindOf(value.owner) !== 'us' && value.owner ? (
          <p className="rounded bg-warning-soft px-2 py-1.5 text-xs text-warning">
            Waiting moves the case to a Waiting lane until the due time or a reply.
          </p>
        ) : null}
        {form.data?.next_move ? (
          <label className="block space-y-1 text-sm font-medium">
            Reason for clearing (optional)
            <Input
              aria-label="Reason for clearing (optional)"
              maxLength={500}
              value={clearReason}
              placeholder="e.g. Work already scheduled"
              onChange={(event) => setClearReason(event.currentTarget.value)}
            />
          </label>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-between gap-2">
          <div>
            {form.data?.next_move ? (
              <Button type="button" variant="destructive" disabled={Boolean(busy)} onClick={() => void clear()}>
                {busy === 'clear' ? 'Clearing…' : 'Clear next move'}
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" disabled={Boolean(busy)} onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={!body || Boolean(busy) || (needsVisitChoice && !visitChoice)}>
              {busy === 'save' ? 'Saving…' : 'Save next move'}
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  )
}
